import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Play, Calendar, Star, MessageSquare, Phone } from 'lucide-react';

export const DemoSection = () => {
  const [activeDemo, setActiveDemo] = useState('scheduling');

  const schedulingSteps = [
    {
      step: 1,
      title: "Patient Calls",
      description: "Sarah calls with lower back pain seeking treatment",
      icon: Phone,
      detail: "AI agent answers immediately, no hold time"
    },
    {
      step: 2,
      title: "Pain Assessment",
      description: "AI gathers pain level, location, and symptom details",
      icon: MessageSquare,
      detail: "Natural language processing identifies pain patterns"
    },
    {
      step: 3,
      title: "Treatment Matching",
      description: "Reviews doctor's expertise and available time slots",
      icon: Calendar,
      detail: "Matches patient needs with appropriate treatment type"
    },
    {
      step: 4,
      title: "Books & Confirms",
      description: "Schedules consultation and sends pre-visit forms",
      icon: Star,
      detail: "Automated SMS with intake forms and directions"
    }
  ];


  return (
    <section id="demo" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-6 mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
            See Our AI Agent
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-green-700">
              {" "}In Action
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Watch how our AI agent handles real patient interactions, from appointment scheduling 
            to care follow-ups, delivering results that exceed expectations.
          </p>
        </div>

        <div className="mb-12">
          <Tabs value={activeDemo} onValueChange={setActiveDemo} className="w-full">
            <TabsList className="grid w-full grid-cols-1 max-w-md mx-auto">
              <TabsTrigger value="scheduling">Treatment Scheduling</TabsTrigger>
            </TabsList>
            
            <div className="mt-12">
              <TabsContent value="scheduling" className="space-y-8">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div className="space-y-8">
                    <h3 className="text-2xl font-bold text-gray-900">
                      Smart Treatment Scheduling Demo
                    </h3>
                    <div className="space-y-6">
                      {schedulingSteps.map((step) => (
                        <div key={step.step} className="flex items-start space-x-4">
                          <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-emerald-600 to-green-700 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">{step.step}</span>
                          </div>
                          <div className="space-y-1">
                            <h4 className="font-semibold text-gray-900">{step.title}</h4>
                            <p className="text-gray-600">{step.description}</p>
                            <p className="text-sm text-emerald-600">{step.detail}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Card className="bg-white shadow-xl">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <Play className="w-5 h-5 text-emerald-600" />
                        <span>Live Scheduling Demo</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="aspect-video bg-gradient-to-br from-emerald-100 to-green-100 rounded-lg flex items-center justify-center">
                        <Button
                          size="lg"
                          className="bg-gradient-to-r from-emerald-600 to-green-700 hover:from-emerald-700 hover:to-green-800 text-white"
                        >
                          <Play className="w-5 h-5 mr-2" />
                          Demo Coming Soon!
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        <div className="text-center">
          <Button
            size="lg"
            className="bg-gradient-to-r from-emerald-600 to-green-700 hover:from-emerald-700 hover:to-green-800 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
            onClick={() => window.open('https://calendly.com/rajat-agents/30min', '_blank')}
          >
            Book Your Personal Demo
          </Button>
        </div>
      </div>
    </section>
  );
};